import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { Camera, Home, BarChart3, Archive, LogOut, Download } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import useInstallPrompt from "@/hooks/useInstallPrompt";

const Layout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { install, canInstall, isIos } = useInstallPrompt();

  const handleInstall = () => {
    if (isIos) {
      toast.info("Su iOS: tocca Condividi → 'Aggiungi a schermata Home'", { duration: 6000 });
    } else {
      install();
    }
  };

  const navItems = [
    { path: "/", icon: Home, label: "Home" },
    { path: "/capture", icon: Camera, label: "Scatta" },
    { path: "/statistics", icon: BarChart3, label: "Stats" },
    { path: "/sold", icon: Archive, label: "Venduti" },
  ];

  const handleLogout = () => {
    logout();
    toast.success("Logout effettuato");
    window.location.href = "/login";
  };

  return (
    <div className="min-h-screen pb-20 md:pb-0">
      {/* Desktop Sidebar */}
      <aside className="hidden md:flex flex-col w-64 border-r border-border h-screen fixed left-0 top-0 p-6 bg-card">
        <div className="mb-8">
          <h1 className="text-2xl font-bold tracking-tight text-foreground">
            <span className="text-primary">Tyre</span>Track
          </h1>
          <p className="text-sm text-muted-foreground mt-1">Gestione Pneumatici</p>
          {user && (
            <p className="text-xs text-primary mt-2 truncate" title={user.displayName}>
              {user.displayName}
            </p>
          )}
        </div>

        <nav className="flex-1 space-y-2">
          {navItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            return (
              <button
                key={item.path}
                data-testid={`nav-${item.label.toLowerCase()}`}
                onClick={() => navigate(item.path)}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-md transition-all touch-target active-scale ${
                  isActive
                    ? "bg-primary text-primary-foreground btn-glow"
                    : "hover:bg-white/5 text-muted-foreground hover:text-foreground"
                }`}
              >
                <Icon size={20} />
                <span className="font-medium">{item.label}</span>
              </button>
            );
          })}
        </nav>

        {/* Logout Button */}
        <div className="pt-4 border-t border-border space-y-3">
          {canInstall && (
            <button
              onClick={handleInstall}
              className="w-full flex items-center gap-3 px-4 py-3 rounded-md transition-all touch-target active-scale hover:bg-primary/10 text-muted-foreground hover:text-primary"
            >
              <Download size={20} />
              <span className="font-medium">Installa App</span>
            </button>
          )}
          <button
            data-testid="nav-logout"
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-4 py-3 rounded-md transition-all touch-target active-scale hover:bg-destructive/10 text-muted-foreground hover:text-destructive"
          >
            <LogOut size={20} />
            <span className="font-medium">Logout</span>
          </button>
          <p className="text-xs text-muted-foreground text-center">
            ©{new Date().getFullYear()} - TyreTrack - v{process.env.REACT_APP_VERSION}
          </p>
        </div>
      </aside>

      {/* Main Content */}
      <main className="md:ml-64 min-h-screen">
        <div className="max-w-5xl mx-auto px-4 py-6 md:px-8 md:py-8">
          <Outlet />
          <p className="text-xs text-muted-foreground text-center mt-6 md:hidden">
            ©{new Date().getFullYear()} - TyreTrack - v{process.env.REACT_APP_VERSION}
          </p>
        </div>
      </main>

      {/* Mobile Bottom Navigation */}
      <nav 
        data-testid="mobile-nav"
        className="fixed bottom-0 left-0 right-0 h-16 glass flex items-center justify-around z-50 md:hidden"
      >
        {navItems.map((item) => {
          const Icon = item.icon;
          const isActive = location.pathname === item.path;
          return (
            <button
              key={item.path}
              data-testid={`mobile-nav-${item.label.toLowerCase()}`}
              onClick={() => navigate(item.path)}
              className={`flex flex-col items-center justify-center gap-1 px-3 py-2 touch-target active-scale ${
                isActive ? "text-primary" : "text-muted-foreground"
              }`}
            >
              <Icon size={20} />
              <span className="text-xs font-medium">{item.label}</span>
            </button>
          );
        })}
        {canInstall && (
          <button
            onClick={handleInstall}
            className="flex flex-col items-center justify-center gap-1 px-3 py-2 touch-target active-scale text-muted-foreground hover:text-primary"
          >
            <Download size={20} />
            <span className="text-xs font-medium">Installa</span>
          </button>
        )}
        <button
          data-testid="mobile-nav-logout"
          onClick={handleLogout}
          className="flex flex-col items-center justify-center gap-1 px-3 py-2 touch-target active-scale text-muted-foreground hover:text-destructive"
        >
          <LogOut size={20} />
          <span className="text-xs font-medium">Esci</span>
        </button>
      </nav>
    </div>
  );
};

export default Layout;
